import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, isToday, isTomorrow, isYesterday, parseISO } from 'date-fns';
import { sk, enUS } from 'date-fns/locale';
import { 
  Calendar, 
  Clock, 
  Plus, 
  Edit, 
  Trash2, 
  Filter,
  Search,
  Bell,
  BellOff,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import QuickReminderForm from '@/components/reminders/QuickReminderForm';
import { useAuth } from '@/hooks/use-auth';

interface Reminder {
  id: number;
  title: string;
  description?: string;
  dueAt: string;
  timezone: string;
  offsets: number[];
  channels: Array<'in_app'|'email'|'push'>;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

type RemindersResponse = Reminder[] | { reminders: Reminder[] };

export default function RemindersPage() {
  const { t, i18n } = useTranslation('reminders');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'today' | 'upcoming' | 'past'>('all');

  // Ak nie je user, zobraz fallback
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="p-4 text-sm text-muted-foreground">Redirecting...</div>
      </div>
    );
  }

  // Prednačítaj reminders balík
  useEffect(() => {
    i18n.loadNamespaces(['reminders']);
  }, []);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);

  // Fetch reminders
  const { data: remindersData, isLoading, error } = useQuery<RemindersResponse>({
    queryKey: ['/api/reminders'],
    enabled: !!user,
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/reminders');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      // Server môže vrátiť { reminders: [...] } alebo priamo pole
      return Array.isArray(data) ? { reminders: data } : data;
    },
    staleTime: 30000, // 30 sekúnd cache
    retry: 2,
  });

  // Delete reminder mutation
  const deleteReminderMutation = useMutation({
    mutationFn: (id: number) => apiRequest('DELETE', `/api/reminders/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/reminders'] });
      queryClient.invalidateQueries({ queryKey: ['reminders'] }); // Pre kompatibilitu
      toast({
        title: t('deleted'),
        description: t('deletedDescription'),
      });
    },
    onError: () => {
      toast({
        title: t('common:error'),
        description: t('deleteError'),
        variant: 'destructive',
      });
    },
  });

  const reminders = Array.isArray(remindersData)
    ? remindersData
    : remindersData?.reminders || [];

  // Filter reminders based on search and status
  const filteredReminders = reminders.filter(reminder => {
    if (reminder.isDeleted) return false;
    
    // Search filter
    if (searchTerm && !reminder.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !reminder.description?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Status filter
    const dueDate = parseISO(reminder.dueAt);
    const now = new Date();
    
    switch (filterStatus) {
      case 'today':
        return isToday(dueDate);
      case 'upcoming':
        return dueDate > now;
      case 'past':
        return dueDate < now && !isToday(dueDate);
      default:
        return true;
    }
  });

  // Group reminders by date
  const groupedReminders = filteredReminders.reduce((groups, reminder) => {
    const dueDate = parseISO(reminder.dueAt);
    const dateKey = format(dueDate, 'yyyy-MM-dd');
    
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(reminder);
    
    return groups;
  }, {} as Record<string, Reminder[]>);

  // Sort groups by date
  const sortedGroups = Object.entries(groupedReminders).sort(([a], [b]) => a.localeCompare(b));

  const getDateLabel = (dateStr: string) => {
    const date = parseISO(dateStr);
    const now = new Date();
    
    if (isToday(date)) {
      return t('today');
    } else if (isTomorrow(date)) {
      return t('tomorrow');
    } else if (isYesterday(date)) {
      return t('yesterday');
    } else {
      const locale = i18n.language === 'sk' ? sk : enUS;
      return format(date, 'EEEE, d. MMMM yyyy', { locale });
    }
  };

  const getTimeLabel = (dateStr: string) => {
    const date = parseISO(dateStr);
    return format(date, 'HH:mm');
  };

  const getStatusBadge = (reminder: Reminder) => {
    const dueDate = parseISO(reminder.dueAt);
    const now = new Date();
    
    if (dueDate < now) {
      return <Badge variant="destructive" className="text-xs"><AlertCircle className="w-3 h-3 mr-1" />{t('overdue')}</Badge>;
    } else if (isToday(dueDate)) {
      return <Badge variant="default" className="text-xs"><Clock className="w-3 h-3 mr-1" />{t('today')}</Badge>;
    } else {
      return <Badge variant="secondary" className="text-xs"><Calendar className="w-3 h-3 mr-1" />{t('upcoming')}</Badge>;
    }
  };

  const getChannelIcons = (channels: Array<'in_app'|'email'|'push'>) => {
    return Array.isArray(channels) ? channels.map(channel => {
      switch (channel) {
        case 'in_app':
          return <Bell className="w-4 h-4" key={channel} />;
        case 'email':
          return <Bell className="w-4 h-4" key={channel} />;
        case 'push':
          return <Bell className="w-4 h-4" key={channel} />;
        default:
          return null;
      }
    }) : [];
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <Card>
          <CardContent className="p-6">
          <div className="text-center text-destructive">
            <AlertCircle className="w-8 h-8 mx-auto mb-2" />
            <p>{t('loadError')}</p>
          </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 section-spacing">
        <div>
          <h1 className="page-title">{t('list.title')}</h1>
          <p className="text-muted-foreground text-lg">{t('list.description')}</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              {t('list.buttons.createNew')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('title')}</DialogTitle>
              <DialogDescription>
                {t('subtitle')}
              </DialogDescription>
            </DialogHeader>
            <QuickReminderForm 
              onSubmit={() => {
                setShowCreateDialog(false);
                queryClient.invalidateQueries({ queryKey: ['/api/reminders'] });
                queryClient.invalidateQueries({ queryKey: ['reminders'] }); // Pre kompatibilitu
              }}
              onCancel={() => setShowCreateDialog(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="section-spacing">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('list.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={(value: any) => setFilterStatus(value)}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder={t('list.filters.all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('list.filters.all')}</SelectItem>
                <SelectItem value="today">{t('today')}</SelectItem>
                <SelectItem value="upcoming">{t('upcoming')}</SelectItem>
                <SelectItem value="past">{t('past')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Reminders List */}
      <div className="section-spacing">
        {sortedGroups.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <div className="text-center text-muted-foreground">
                <BellOff className="w-12 h-12 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t('noReminders')}</h3>
                <p>{t('noRemindersDescription')}</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
          {Array.isArray(sortedGroups) ? sortedGroups.map(([dateKey, reminders]) => (
            <div key={dateKey}>
              <h2 className="text-xl font-semibold mb-4 text-foreground">
                {getDateLabel(dateKey)}
              </h2>
              <div className="space-y-3">
                {Array.isArray(reminders) ? reminders.map((reminder) => (
                  <Card key={reminder.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex flex-col xs:flex-row xs:items-start xs:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="font-semibold break-words">{reminder.title}</h3>
                            {getStatusBadge(reminder)}
                          </div>
                          {reminder.description && (
                            <p className="text-muted-foreground text-sm mb-2">
                              {reminder.description}
                            </p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {getTimeLabel(reminder.dueAt)}
                            </div>
                            <div className="flex items-center gap-1">
                              {getChannelIcons(reminder.channels)}
                            </div>
                            {reminder.offsets.length > 0 && (
                              <div className="text-xs">
                                {Array.isArray(reminder.offsets) ? reminder.offsets.map(offset => {
                                  if (offset < 60) return `${offset}min`;
                                  if (offset < 1440) return `${Math.floor(offset / 60)}h`;
                                  return `${Math.floor(offset / 1440)}d`;
                                }).join(', ') : ''} {t('before')}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingReminder(reminder);
                              setShowEditDialog(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteReminderMutation.mutate(reminder.id)}
                            disabled={deleteReminderMutation.isPending}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )) : null}
              </div>
            </div>
          )) : null}
          </div>
        )}
      </div>

      {/* Edit Reminder Dialog */}
      {editingReminder && (
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('edit') || 'Upraviť pripomienku'}</DialogTitle>
              <DialogDescription>
                {t('editDescription') || 'Upravte údaje pripomienky'}
              </DialogDescription>
            </DialogHeader>
            <QuickReminderForm 
              onSubmit={() => {
                setShowEditDialog(false);
                setEditingReminder(null);
                queryClient.invalidateQueries({ queryKey: ['/api/reminders'] });
                queryClient.invalidateQueries({ queryKey: ['reminders'] }); // Pre kompatibilitu
              }}
              onCancel={() => {
                setShowEditDialog(false);
                setEditingReminder(null);
              }}
              defaultValues={{
                id: editingReminder.id,
                title: editingReminder.title,
                description: editingReminder.description || '',
                dueAt: editingReminder.dueAt,
                timezone: editingReminder.timezone,
                offsets: editingReminder.offsets,
                channels: editingReminder.channels,
              }}
              isEdit={true}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
